import { forwardRef } from 'react';

export interface ToggleSwitchProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

const sizes = {
  // Thumb is anchored with left padding; translate moves it to the right.
  sm: { track: 'h-5 w-9', thumb: 'h-4 w-4', on: 'translate-x-[18px]', off: 'translate-x-0' },
  md: { track: 'h-6 w-11', thumb: 'h-5 w-5', on: 'translate-x-[22px]', off: 'translate-x-0' },
} as const;

const ToggleSwitch = forwardRef<HTMLButtonElement, ToggleSwitchProps>(function ToggleSwitch(
  { checked, onCheckedChange, label, description, disabled = false, size = 'md', className = '', ...props },
  ref
) {
  const s = sizes[size];
  const id = props.id ?? (label ? label.toLowerCase().replace(/\s/g, '-') : undefined);

  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      {(label || description) && (
        <div className="min-w-0">
          {label && (
            <label htmlFor={id} className="block text-sm font-semibold text-slate-900">
              {label}
            </label>
          )}
          {description && <p className="mt-0.5 text-xs text-slate-600">{description}</p>}
        </div>
      )}

      <button
        {...props}
        ref={ref}
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          props.onClick?.(e);
          if (e.defaultPrevented || disabled) return;
          onCheckedChange(!checked);
        }}
        onKeyDown={(e) => {
          props.onKeyDown?.(e);
          if (e.defaultPrevented || disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCheckedChange(!checked);
          }
        }}
        className={`relative shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
          checked ? 'bg-primary-600' : 'bg-slate-300'
        } ${s.track}`}
      >
        <span
          className={`absolute left-0.5 top-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform ${s.thumb} ${
            checked ? s.on : s.off
          }`}
        />
      </button>
    </div>
  );
});

export default ToggleSwitch;

